import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import CustomInputField from "components/common/CustomInputField";
import { successToaster, ErrorToaster } from "components/common/toast";
import handleError from "utils/handleError";
import BtnCustom from "components/common/btn-custom";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CustomPaginatedSelect from "components/common/CustomPaginatedSelect";
import { Button, InputGroup } from "reactstrap";
import ImageUploadCustom from "components/CustomUpload/ImageUploadCustom";
import { WineDataFieldsOrdered as WineDataFields, WIDE_TEXT_FIELDS } from "constant/Fields";
import { AttributesImageFields } from "constant/Fields";
import { UpdateDataSet } from "network/wineDataSetAPI";
import { CreateDataSet } from "network/wineDataSetAPI";
import PATHS from "constant/paths";
import { WineDataSetSchema } from "components/schema/WineDataSetSchema";
import { formatDecimalWithComma } from "utils/formatDecimalWithComma";
import { ImageUrlToFile } from "utils/ImageUrlToFile";
import CountryListSelect from "components/common/CounteryListSlecte";
import { ShowLoadingScreen } from "components/common/Loading";
import { GetSingleUnapprovedDataSet } from "network/wineDataSetAPI";
import { UpdateUnapproved, AIProposeRequest } from "network/wineDataSetAPI";
import { mediaUrl } from "utils/mediaUrl";

const EditUnapprovedWineData = () => {
  const { t } = useTranslation("wine");
  const navigate = useNavigate();
  const [errorImg, setErrorImg] = useState({});
  const { id } = useParams();
  const [editData, setEditData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [asking, setAsking] = useState(false);

  async function getSingleData(editID) {
    try {
      const response = await GetSingleUnapprovedDataSet(editID);
      if (response.success) {
        setEditData(response.data);
      }
    } catch (errors) {
    } finally {
      setIsLoading(false);
    }
  }
  useEffect(() => {
    getSingleData(id);
  }, [id]);

  const initialValues = {
    name: editData?.attributes?.name || "",
    description: editData?.attributes?.description || "",
    front_bottle_image: editData?.attributes?.front_bottle_image
      ? mediaUrl(editData?.attributes?.front_bottle_image)
      : null,
    back_bottle_image: editData?.attributes?.back_bottle_image
      ? mediaUrl(editData?.attributes?.back_bottle_image)
      : null,
    front_label_image: editData?.attributes?.front_label_image
      ? mediaUrl(editData?.attributes?.front_label_image)
      : null,
    back_label_image: editData?.attributes?.back_label_image
      ? mediaUrl(editData?.attributes?.back_label_image)
      : null,
    ean: editData?.attributes?.ean || "",
    winery: editData?.attributes?.winery || "",
    vintage: formatDecimalWithComma(editData?.attributes?.vintage) || "",
    denomination: editData?.attributes?.denomination || "",
    gradation: formatDecimalWithComma(editData?.attributes?.gradation) || "",
    vinification: editData?.attributes?.vinification || "",
    terroir: editData?.attributes?.terroir || "",
    region: editData?.attributes?.region || "",
    format: formatDecimalWithComma(editData?.attributes?.format) || "",
    country: editData?.attributes?.country ?? null,
    natural: editData?.attributes?.natural ?? null,
    bio: editData?.attributes?.bio ?? null,
    have_sulphite: editData?.attributes?.have_sulphite ?? null,
    biodynamic: editData?.attributes?.biodynamic ?? null,
    conventional: editData?.attributes?.conventional ?? null,
    energy: editData?.attributes?.energy || "",
    fat: formatDecimalWithComma(editData?.attributes?.fat) || "",
    saturated_fat:
      formatDecimalWithComma(editData?.attributes?.saturated_fat) || "",
    carbohydrate:
      formatDecimalWithComma(editData?.attributes?.carbohydrate) || "",
    sugar: formatDecimalWithComma(editData?.attributes?.sugar) || "",
    protein: formatDecimalWithComma(editData?.attributes?.protein) || "",
    salt: formatDecimalWithComma(editData?.attributes?.salt) || "",
    ingredients: editData?.attributes?.ingredients || "",
    fermentation: editData?.attributes?.fermentation || "",
    colour: editData?.attributes?.colour || "",
    appearance: editData?.attributes?.appearance || "",
    aroma: editData?.attributes?.aroma || "",
    taste: editData?.attributes?.taste || "",
    pairing: editData?.attributes?.pairing || "",

    // is_active: editData?.attributes?.is_active ?? true,
    // is_approved: editData?.attributes?.is_approved ?? true,
  };
  const convertToFormData = async (values) => {
    const formData = new FormData();

    const imageFields = [
      "front_bottle_image",
      "back_bottle_image",
      "front_label_image",
      "back_label_image",
    ];
    await Promise.all(
      imageFields.map(async (field) => {
        const value = values[field];
        if (value instanceof File) {
          formData.append(field, value);
        } else if (typeof value === "string" && value.startsWith("http")) {
          const file = await ImageUrlToFile(value);
          if (file) {
            formData.append(field, file);
          } else {
            formData.append(field, "");
          }
        } else {
          formData.append(field, "");
        }
      })
    );

    if (values.sub_categories && Array.isArray(values.sub_categories)) {
      values.sub_categories.forEach((item) => {
        formData.append("sub_categories", item?.value);
      });
    }

    Object.keys(values).forEach((key) => {
      if (!imageFields.includes(key) && key !== "sub_categories") {
        const value = values[key];

        if (value instanceof File || value instanceof Blob) {
          formData.append(key, value);
        } else if (Array.isArray(value)) {
          value.forEach((item, index) => {
            formData.append(`${key}[${index}]`, item);
          });
        } else if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      }
    });

    return formData;
  };
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    resetForm,
  } = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: WineDataSetSchema,
    onSubmit: async (values) => {
      const formData = await convertToFormData(values);

      try {
        const response = await UpdateUnapproved(id, formData);

        if (response.success) {
          successToaster(response.message);
          navigate(`${PATHS.WINE_DATASET_UNAPPROVED_VIEW}/${id}`);
          resetForm();
        }
      } catch (error) {
        ErrorToaster(handleError(error?.status_code, error?.message));
      }
    },
  });

  if (isLoading) {
    return <ShowLoadingScreen />;
  }
  // L'AI propone solo i campi che nel modulo sono ancora vuoti: quello che hai gia' scritto non si tocca.
  const askAI = async () => {
    setAsking(true);
    try {
      const res = await AIProposeRequest(id);
      const proposal = res?.data?.proposal || {};
      const numeric = ["gradation", "format"];
      let filled = 0;
      Object.entries(proposal).forEach(([key, value]) => {
        if (!(key in values)) return; // es. paese/uvaggio: restano salvati nella richiesta
        const current = values[key];
        if (current !== "" && current !== null && current !== undefined) return;
        setFieldValue(key, numeric.includes(key) ? formatDecimalWithComma(value) : value);
        filled += 1;
      });
      successToaster(
        filled
          ? t("form.aiFilled", { count: filled })
          : t("form.aiNothing")
      );
    } catch (error) {
      ErrorToaster(handleError(error?.status_code, error?.message));
    } finally {
      setAsking(false);
    }
  };

  return (
    <div className="content my-4">
      {editData?.attributes ? (
        <>
          <div className="d-flex mb-4 flex-wrap gap-16 align-items-center justify-content-between col-reverse">
            <Button
              onClick={() => navigate(-1)}
              className="btn btn-primary d-flex align-items-center align-self-center"
            >
              <i
                className="tim-icons icon-minimal-left
            
            font-weight-bold mr-2  font-size-xl"
              ></i>
              <span>{t("common.back")}</span>
            </Button>
            <Button
              type="button"
              className="btn btn-info align-self-center"
              disabled={asking}
              onClick={askAI}
            >
              {asking ? t("common.askingAI") : t("common.askAI")}
            </Button>
            <h2 className="text-capitalize mb-0">{t("form.updateTitle")}</h2>
          </div>

          <div className="mt-2">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {WineDataFields.map(
                  ({ name, type, options, unit, required, staticOptions1 }) => {
                    return (
                      <div
                        key={name}
                        className={`col-12 mb-2 ${WIDE_TEXT_FIELDS.includes(name) ? "" : "col-sm-6 col-md-6 col-lg-3"}`}
                      >
                        {(type === "text" || type === "textarea") && (
                          <CustomInputField
                            label={`${t(`fields.${name}`)}${
                              required ? " *" : ""
                            }`}
                            name={name}
                            placeholder={t(`fields.${name}`)}
                            type={type}
                            errors={errors}
                            touched={touched}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values}
                            unit={unit}
                            {...(WIDE_TEXT_FIELDS.includes(name) ? { rows: name === "description" ? 5 : 3 } : {})}
                          />
                        )}
                        {type === "select" && (
                          <CustomPaginatedSelect
                            key={name}
                            label={t(`fields.${name}`).toUpperCase()}
                            name={name}
                            options={options}
                            errors={errors}
                            touched={touched}
                            isSearchable={false}
                            onChange={(selectedOption) => {
                              setFieldValue(
                                name,
                                selectedOption?.value ?? null
                              );
                            }}
                            onBlur={handleBlur}
                            value={
                              staticOptions1.find((opt) => {
                                const optionValue =
                                  values[name] === true
                                    ? true
                                    : values[name] === false
                                    ? false
                                    : null;
                                return opt.value === optionValue;
                              }) || null
                            }
                            staticOptions={staticOptions1}
                          />
                        )}
                        {type === "selectCountry" && (
                          <CountryListSelect
                            label={t("fields.country")}
                            name="country"
                            errors={errors}
                            touched={touched}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            setFieldValue={setFieldValue}
                            value={values["country"]}
                          />
                        )}
                      </div>
                    );
                  }
                )}
              </div>
              <div className="row">
                {AttributesImageFields?.map(({ name }) => {
                  return (
                    <div
                      key={name}
                      className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 "
                    >
                      <div className="custom_input">
                        <InputGroup>
                          <ImageUploadCustom
                            label={t(`fields.${name}`).toUpperCase()}
                            name={name}
                            changeBtnClasses="btn-simple"
                            addBtnClasses="btn-simple"
                            removeBtnClasses="btn-simple"
                            setFieldValue={setFieldValue}
                            value={values[name]}
                            errors={errorImg}
                            setError={setErrorImg}
                          />
                        </InputGroup>
                      </div>
                    </div>
                  );
                })}
              </div>

              <BtnCustom
                isLoading={isSubmitting}
                disabled={isSubmitting}
                type="submit"
                color="btn btn-primary mt-4 mr-3 block"
                submittingText={t("form.updating")}
              >
                {t("form.updateButton")}
              </BtnCustom>
            </form>
          </div>
        </>
      ) : (
        <h2 className="text-center mt-5">{t("form.noRecords")}</h2>
      )}
    </div>
  );
};

export default EditUnapprovedWineData;
